import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { DataProfile, EventHistoryItem, updateClientProfile } from '@/services/clientProfile';
import { MyCommunityItem } from '@/services/communityService';
import showToast from '@/components/ui/Toast';
import { ALL_RACKET_METRICS_LIST } from '@/pages/client/ClientRacketStatsSettingsPage';

export interface ClientRacketProfileViewProps {
  profile: DataProfile | null;
  avatarUrl: string;
  displayName: string;
  username: string;
  locationDisplay: string;
  ageDisplay: string;
  dominantFootDisplay: string;
  heightDisplay: string;
  activeStatSport: string;
  setActiveStatSport: (sport: string) => void;
  activeHistoryTab: 'mabar' | 'sparing';
  setActiveHistoryTab: (tab: 'mabar' | 'sparing') => void;
  galleryPhotos: string[];
  communities?: MyCommunityItem[];
  isLoadingCommunities?: boolean;
  eventHistoryList?: EventHistoryItem[];
  isLoadingHistory?: boolean;
  openAvatarModal: () => void;
  openEditBasicModal: () => void;
  openEditModal: () => void;
  openGalleryModal: () => void;
  handlePhotoClick: (index: number) => void;
  handleEventDetailClick?: (item: EventHistoryItem) => void;
  onProfileUpdate?: () => void;
}

export const ClientRacketProfileView: React.FC<ClientRacketProfileViewProps> = ({
  profile,
  avatarUrl,
  displayName,
  username,
  locationDisplay,
  ageDisplay,
  dominantFootDisplay,
  heightDisplay,
  activeStatSport,
  setActiveStatSport,
  activeHistoryTab,
  setActiveHistoryTab,
  galleryPhotos,
  communities = [],
  isLoadingCommunities = false,
  eventHistoryList = [],
  isLoadingHistory = false,
  openAvatarModal,
  openEditBasicModal,
  openEditModal,
  openGalleryModal,
  handlePhotoClick,
  handleEventDetailClick,
  onProfileUpdate,
}) => {
  const navigate = useNavigate();

  // Active Cabor settings logic
  const ALL_RACKET_SPORTS = ['badminton', 'padel', 'tenis'];
  const rawActiveSports: string[] = profile?.information?.active_racket_sports;
  const activeRacketSports: string[] =
    Array.isArray(rawActiveSports) && rawActiveSports.length > 0
      ? rawActiveSports
      : ALL_RACKET_SPORTS;

  const [isCaborModalOpen, setIsCaborModalOpen] = useState(false);
  const [tempSelectedSports, setTempSelectedSports] = useState<string[]>(activeRacketSports);
  const [isSavingCabor, setIsSavingCabor] = useState(false);

  const toggleSport = (sportKey: string) => {
    setTempSelectedSports((prev) => {
      if (prev.includes(sportKey)) {
        return prev.filter((s) => s !== sportKey);
      } else {
        return [...prev, sportKey];
      }
    });
  };

  const handleSaveCabor = async () => {
    try {
      setIsSavingCabor(true);
      const updatedInformation = {
        ...(profile?.information || {}),
        active_racket_sports: tempSelectedSports,
      };
      await updateClientProfile({ information: updatedInformation });
      showToast.success('Pengaturan cabor berhasil disimpan');
      setIsCaborModalOpen(false);
      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (err) {
      showToast.error('Gagal menyimpan pengaturan cabor');
    } finally {
      setIsSavingCabor(false);
    }
  };

  return (
    <>
      {/* 1. HERO COVER & PROFILE IMAGE CARD */}
      <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-2xs overflow-hidden">
        {/* Main Cover Photo Box */}
        <div
          className="relative w-full aspect-square md:aspect-[4/3] rounded-t-3xl bg-slate-900 bg-cover bg-center overflow-hidden flex flex-col justify-between p-4 md:p-6 text-white"
          style={{
            backgroundImage: `url("${avatarUrl}")`,
          }}
        >
          {/* Dark Overlay Gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/30 pointer-events-none" />

          {/* Top Row Action Buttons */}
          <div className="relative z-10 flex items-center justify-between">
            {/* Camera Button on the Left */}
            <button
              type="button"
              onClick={openAvatarModal}
              className="w-11 h-11 rounded-2xl bg-white/20 hover:bg-white/30 text-white transition-all flex items-center justify-center cursor-pointer backdrop-blur-md border border-white/20 shadow-sm"
              aria-label="Ubah Foto Profil"
            >
              <Icon icon="ph:camera-bold" className="w-5 h-5 text-white" />
            </button>

            {/* Top Right Menu Button */}
            <button
              type="button"
              onClick={() => navigate('/profile/account')}
              className="w-11 h-11 rounded-2xl bg-white/20 hover:bg-white/30 text-white transition-all flex items-center justify-center cursor-pointer backdrop-blur-md border border-white/20 shadow-sm"
              aria-label="Pengaturan Akun"
            >
              <Icon icon="ph:list-bold" className="w-5.5 h-5.5 text-white" />
            </button>
          </div>

          {/* Bottom Hero Info */}
          <div className="relative z-10 space-y-2.5">
            {/* Name & Pencil */}
            <div className="flex items-center gap-2">
              <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                {displayName}
              </h1>
              <button
                type="button"
                onClick={openEditBasicModal}
                className="w-7 h-7 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer"
                aria-label="Edit Nama"
              >
                <Icon icon="ph:pencil-simple-bold" className="w-4 h-4" />
              </button>
            </div>

            {/* Username & City */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-200 font-medium">
              <span>{username}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 text-gray-300" />
                {locationDisplay !== '-' ? locationDisplay : 'Yogyakarta'}
              </span>
            </div>
          </div>
        </div>

        {/* Stat Summary Bar (Attached below Cover) */}
        <div className="grid grid-cols-3 divide-x divide-gray-100 p-4 text-center">
          <div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 block mb-0.5">
              EVENTS
            </span>
            <span className="text-xl md:text-2xl font-bold text-[#0E1726]">
              {profile?.stats?.events && profile.stats.events > 0 ? profile.stats.events : '-'}
            </span>
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 block mb-0.5">
              MATCHES
            </span>
            <span className="text-xl md:text-2xl font-bold text-[#0E1726]">
              {profile?.stats?.matches && profile.stats.matches > 0 ? profile.stats.matches : '-'}
            </span>
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 block mb-0.5">
              WINRATE
            </span>
            <span className="text-xl md:text-2xl font-bold text-[#1D4ED8]">
              {profile?.stats?.matches && profile.stats.matches > 0 && profile?.stats?.win_rate !== undefined
                ? `${profile.stats.win_rate}%`
                : '-'}
            </span>
          </div>
        </div>
      </div>

      {/* 2. CABOR YANG KAMU MAINKAN CARD */}
      <div
        onClick={() => {
          setTempSelectedSports(activeRacketSports);
          setIsCaborModalOpen(true);
        }}
        className="bg-white rounded-3xl p-4 border border-[#E0E6ED] shadow-2xs flex items-center justify-between cursor-pointer hover:bg-gray-50/80 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[#ECF2FE] text-[#1D4ED8] flex items-center justify-center shrink-0">
            <Icon icon="ph:sliders-horizontal" className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#0E1726]">Cabor yang kamu mainkan</h3>
            <p className="text-xs text-gray-400 font-medium mt-0.5">
              {activeRacketSports.length} dari 3 cabor aktif
            </p>
          </div>
        </div>
        <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-400" />
      </div>

      {/* 3. INFO PEMAIN */}
      <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-[#0E1726]">Info pemain</h3>
          <button
            type="button"
            onClick={openEditModal}
            className="h-8 px-3.5 rounded-full bg-[#ECF2FE] text-[#1D4ED8] hover:bg-[#DBE6FE] text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5" />
            <span>Edit</span>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* Nomor favorit / Tipe */}
          <div className=" rounded-2xl p-3.5 space-y-1">
            <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
              <Icon icon="ph:users-bold" className="w-3.5 h-3.5 text-gray-400" />
              Nomor favorit
            </span>
            <span className="text-sm font-bold text-[#0E1726] block">
              {profile?.information?.racket_position || profile?.information?.play_type || '-'}
            </span>
          </div>

          {/* Umur */}
          <div className=" rounded-2xl p-3.5 space-y-1">
            <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
              <Icon icon="ph:clock-bold" className="w-3.5 h-3.5 text-gray-400" />
              Umur
            </span>
            <span className="text-sm font-bold text-[#0E1726] block">{ageDisplay !== '-' ? ageDisplay : '-'}</span>
          </div>

          {/* Tangan dominan */}
          <div className=" rounded-2xl p-3.5 space-y-1">
            <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
              <Icon icon="ph:hand-palm-bold" className="w-3.5 h-3.5 text-gray-400" />
              Tangan dominan
            </span>
            <span className="text-sm font-bold text-[#0E1726] block">
              {profile?.information?.dominant_hand || dominantFootDisplay}
            </span>
          </div>

          {/* Tinggi badan */}
          <div className=" rounded-2xl p-3.5 space-y-1">
            <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
              <Icon icon="ph:arrows-out-cardinal-bold" className="w-3.5 h-3.5 text-gray-400" />
              Tinggi badan
            </span>
            <span className="text-sm font-bold text-[#0E1726] block">
              {heightDisplay !== '-' ? heightDisplay : '-'}
            </span>
          </div>
        </div>
      </div>

      {/* 4. KLUB & KOMUNITAS */}
      {isLoadingCommunities ? (
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
          <div className="flex items-center justify-between">
            <div className="h-5 w-24 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-8 w-20 bg-gray-200 rounded-full animate-pulse" />
          </div>
          <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-none">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex flex-col items-center gap-2 shrink-0 w-16 animate-pulse">
                <div className="w-14 h-14 rounded-full bg-gray-200" />
                <div className="h-3 w-12 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-[#0E1726]">Klub & komunitas</h3>
            <button
              type="button"
              onClick={() => navigate('/my-community')}
              className="h-8 px-3.5 rounded-full bg-[#ECF2FE] text-[#1D4ED8] hover:bg-[#DBE6FE] text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <Icon icon="ph:sliders-horizontal" className="w-3.5 h-3.5" />
              <span>Manage</span>
            </button>
          </div>

          {communities.length > 0 ? (
            <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-none">
              {communities.map((comm) => {
                const initial = comm.name ? comm.name.substring(0, 2).toUpperCase() : 'KM';
                return (
                  <div
                    key={comm.slug}
                    onClick={() => navigate(`/community/${comm.slug}`)}
                    className="flex flex-col items-center gap-1.5 shrink-0 w-16 text-center cursor-pointer group"
                  >
                    {comm.logo ? (
                      <div className="w-14 h-14 rounded-full border-2 border-[#1D4ED8] overflow-hidden shadow-2xs group-hover:scale-105 transition-transform bg-gray-100">
                        <img src={comm.logo} alt={comm.name} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-full border-2 border-[#1D4ED8] bg-[#ECF2FE] flex items-center justify-center text-sm font-bold text-[#1D4ED8] shadow-2xs group-hover:scale-105 transition-transform">
                        {initial}
                      </div>
                    )}
                    <span className="text-[11px] font-semibold text-gray-600 leading-tight truncate w-full">
                      {comm.name}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-6 px-4 rounded-2xl border-2 border-dashed border-gray-200 text-center flex flex-col items-center justify-center space-y-3 bg-gray-50/50">
              <div className="w-12 h-12 rounded-full bg-[#ECF2FE] text-[#1D4ED8] flex items-center justify-center">
                <Icon icon="ph:users-three-bold" className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-[#0E1726]">Belum bergabung ke komunitas</h4>
                <p className="text-xs text-gray-400 max-w-xs">
                  Temukan dan bergabunglah dengan komunitas olahraga favoritmu di sekitarmu!
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/discovery/community')}
                className="h-8 px-4 rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs mt-1"
              >
                <Icon icon="ph:magnifying-glass-bold" className="w-3.5 h-3.5" />
                <span>Cari Komunitas</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* 5. RINGKASAN STATISTIK */}
      <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-[#0E1726]">Ringkasan statistik</h3>
          <button
            type="button"
            onClick={() => navigate('/profile/stats')}
            className="h-8 px-3.5 rounded-full bg-[#ECF2FE] text-[#1D4ED8] hover:bg-[#DBE6FE] text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <Icon icon="ph:sliders-horizontal" className="w-3.5 h-3.5" />
            <span>Manage</span>
          </button>
        </div>

        {/* Sport Selector Pills */}
        <div className="bg-[#ECF2FE] p-1 rounded-2xl flex items-center justify-between gap-1 text-xs font-semibold text-gray-500">
          {activeRacketSports.includes('badminton') && (
            <button
              type="button"
              onClick={() => setActiveStatSport('badminton')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeStatSport === 'badminton'
                  ? 'bg-white text-[#1D4ED8] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Badminton
            </button>
          )}
          {activeRacketSports.includes('padel') && (
            <button
              type="button"
              onClick={() => setActiveStatSport('padel')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeStatSport === 'padel'
                  ? 'bg-white text-[#1D4ED8] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Padel
            </button>
          )}
          {activeRacketSports.includes('tenis') && (
            <button
              type="button"
              onClick={() => setActiveStatSport('tenis')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeStatSport === 'tenis' || activeStatSport === 'tennis'
                  ? 'bg-white text-[#1D4ED8] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Tenis
            </button>
          )}
        </div>

        {/* 2x2 Grid Stat Cards */}
        {(() => {
          const activeConfiguredKeys: string[] =
            profile?.information?.stat_config?.[activeStatSport] ||
            JSON.parse(localStorage.getItem(`sporto_stat_config_${activeStatSport}`) || 'null') ||
            ['poin', 'point_loss', 'total_menang', 'total_kalah'];

          const sportStatData = profile?.sport_stats?.[activeStatSport];

          return (
            <div className="grid grid-cols-2 gap-3">
              {activeConfiguredKeys.map((mKey) => {
                const mDef = ALL_RACKET_METRICS_LIST.find((m) => m.key === mKey) || {
                  key: mKey,
                  label: mKey,
                  icon: 'ph:chart-bar-bold',
                  bg: 'bg-[#ECF2FE]',
                  text: 'text-[#1D4ED8]',
                  getValue: (s: any) => s?.[mKey] ?? 0,
                };
                const val = mDef.getValue(sportStatData);

                return (
                  <div
                    key={mKey}
                    className="bg-white border border-[#E0E6ED] rounded-2xl p-3.5 flex items-center gap-3 shadow-2xs"
                  >
                    <div className={`w-10 h-10 rounded-2xl ${mDef.bg} ${mDef.text} flex items-center justify-center shrink-0`}>
                      <Icon icon={mDef.icon} className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-xl font-bold text-[#0E1726] leading-tight block">{val}</span>
                      <span className="text-xs text-gray-400 font-medium leading-tight block">{mDef.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* 6. RIWAYAT EVENT */}
      <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
        <h3 className="text-base font-bold text-[#0E1726]">Riwayat event</h3>

        {/* Tabs */}
        <div className="bg-[#ECF2FE] p-1 rounded-2xl flex items-center justify-between gap-1 text-xs font-semibold text-gray-500">
          <button
            type="button"
            onClick={() => setActiveHistoryTab('mabar')}
            className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
              activeHistoryTab === 'mabar'
                ? 'bg-white text-[#1D4ED8] shadow-2xs font-bold'
                : 'hover:text-[#0E1726]'
            }`}
          >
            Mabar
          </button>
          <button
            type="button"
            onClick={() => setActiveHistoryTab('sparing')}
            className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
              activeHistoryTab === 'sparing'
                ? 'bg-white text-[#1D4ED8] shadow-2xs font-bold'
                : 'hover:text-[#0E1726]'
            }`}
          >
            Sparing
          </button>
        </div>

        {/* List of History Items */}
        <div className="divide-y divide-gray-100 space-y-2">
          {isLoadingHistory ? (
            <div className="space-y-3 pt-1">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between gap-3 animate-pulse pt-2">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-2xl bg-gray-200 shrink-0" />
                    <div className="space-y-1.5 flex-1 min-w-0">
                      <div className="h-4 w-32 bg-gray-200 rounded" />
                      <div className="h-3 w-44 bg-gray-200 rounded" />
                    </div>
                  </div>
                  <div className="h-8 w-16 rounded-full bg-gray-200 shrink-0" />
                </div>
              ))}
            </div>
          ) : eventHistoryList.length === 0 ? (
            <div className="py-6 text-center text-xs text-gray-400 font-medium">
              Belum ada riwayat event {activeHistoryTab === 'sparing' ? 'sparing' : 'mabar'} untuk {activeStatSport}.
            </div>
          ) : (
            eventHistoryList.map((item) => {
              const dateStr = item.event_date
                ? new Date(item.event_date).toLocaleDateString('id-ID', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })
                : '';
              const locationStr = item.lokasi_arena || item.city || '';
              const subtitle = [locationStr, item.city !== locationStr ? item.city : '', dateStr]
                .filter(Boolean)
                .join(' · ');

              return (
                <div key={item.id} className="pt-2 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {item.host_avatar ? (
                      <img
                        src={item.host_avatar}
                        alt={item.host_name || item.name}
                        className="w-10 h-10 rounded-2xl object-cover shrink-0 border border-gray-100 shadow-2xs"
                      />
                    ) : (
                      <div
                        className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-xs shrink-0 ${
                          item.badge_bg || 'bg-[#1D4ED8] text-white'
                        }`}
                      >
                        {item.host_name ? item.host_name.charAt(0).toUpperCase() : (item.badge || 'MB')}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h4 className="text-xs font-bold text-[#0E1726] truncate">{item.name}</h4>
                      <p className="text-[11px] text-gray-400 truncate mt-0.5">{subtitle}</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleEventDetailClick && handleEventDetailClick(item)}
                    className="h-8 px-3 rounded-full bg-[#ECF2FE] hover:bg-[#DBE6FE] text-[#1D4ED8] text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer shrink-0"
                  >
                    <span>Detail</span>
                    <Icon icon="ph:caret-right-bold" className="w-3 h-3 text-[#1D4ED8]" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 9. GALERI */}
      <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-[#0E1726]">Galeri</h3>
          <button
            type="button"
            onClick={openGalleryModal}
            className="h-8 px-3.5 rounded-full bg-[#ECF2FE] text-[#1D4ED8] hover:bg-[#DBE6FE] text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <Icon icon="ph:sliders-horizontal" className="w-3.5 h-3.5" />
            <span>Manage</span>
          </button>
        </div>

        {galleryPhotos.length > 0 ? (
          <div className="grid grid-cols-3 gap-2.5">
            {galleryPhotos.map((photo, idx) => (
              <div
                key={idx}
                onClick={() => handlePhotoClick(idx)}
                className="aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 hover:opacity-90 transition-opacity cursor-pointer"
              >
                <img src={photo} alt={`Galeri ${idx + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2.5">
            {[
              'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&w=400&q=80',
              'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?auto=format&fit=crop&w=400&q=80',
              'https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?auto=format&fit=crop&w=400&q=80',
              'https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?auto=format&fit=crop&w=400&q=80',
              'https://images.unsplash.com/photo-1511067007398-7e4b90cfa4bc?auto=format&fit=crop&w=400&q=80',
              'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80',
            ].map((photo, idx) => (
              <div
                key={idx}
                onClick={() => handlePhotoClick(idx)}
                className="aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 hover:opacity-90 transition-opacity cursor-pointer"
              >
                <img src={photo} alt={`Galeri ${idx + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CABOR MODAL BOTTOM SHEET */}
      {isCaborModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-xs p-0 sm:p-4 animate-in fade-in duration-200"
          onClick={() => setIsCaborModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5 md:p-6 space-y-5 shadow-2xl animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto" />

            {/* Header */}
            <div className="space-y-1">
              <h2 className="text-lg font-bold text-[#0E1726]">
                Cabor yang kamu mainkan
              </h2>
              <p className="text-xs text-gray-400 leading-relaxed font-medium">
                Hanya cabor yang aktif yang tampil di profil dan punya level sendiri. Matikan cabor yang tidak kamu mainkan.
              </p>
            </div>

            {/* Options List */}
            <div className="border border-gray-200/90 rounded-2xl divide-y divide-gray-100 overflow-hidden bg-white">
              {/* Badminton */}
              <div
                onClick={() => toggleSport('badminton')}
                className="p-3.5 flex items-center justify-between cursor-pointer hover:bg-gray-50/70 transition-colors"
              >
                <div className="space-y-0.5">
                  <h4 className="text-xs font-bold text-[#0E1726]">Badminton</h4>
                  <p className="text-[11px] text-gray-400 font-medium">
                    Level & poin badminton kamu
                  </p>
                </div>
                <div
                  className={`w-11 h-6 flex items-center rounded-full p-0.5 transition-colors cursor-pointer ${
                    tempSelectedSports.includes('badminton') ? 'bg-[#1D4ED8]' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform ${
                      tempSelectedSports.includes('badminton') ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </div>
              </div>

              {/* Padel */}
              <div
                onClick={() => toggleSport('padel')}
                className="p-3.5 flex items-center justify-between cursor-pointer hover:bg-gray-50/70 transition-colors"
              >
                <div className="space-y-0.5">
                  <h4 className="text-xs font-bold text-[#0E1726]">Padel</h4>
                  <p className="text-[11px] text-gray-400 font-medium">
                    Level & poin padel kamu
                  </p>
                </div>
                <div
                  className={`w-11 h-6 flex items-center rounded-full p-0.5 transition-colors cursor-pointer ${
                    tempSelectedSports.includes('padel') ? 'bg-[#1D4ED8]' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform ${
                      tempSelectedSports.includes('padel') ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </div>
              </div>

              {/* Tenis */}
              <div
                onClick={() => toggleSport('tenis')}
                className="p-3.5 flex items-center justify-between cursor-pointer hover:bg-gray-50/70 transition-colors"
              >
                <div className="space-y-0.5">
                  <h4 className="text-xs font-bold text-[#0E1726]">Tenis</h4>
                  <p className="text-[11px] text-gray-400 font-medium">
                    Level & poin tenis kamu
                  </p>
                </div>
                <div
                  className={`w-11 h-6 flex items-center rounded-full p-0.5 transition-colors cursor-pointer ${
                    tempSelectedSports.includes('tenis') ? 'bg-[#1D4ED8]' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform ${
                      tempSelectedSports.includes('tenis') ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Bottom Action Button */}
            <div className="pt-2">
              <button
                type="button"
                disabled={isSavingCabor}
                onClick={handleSaveCabor}
                className="w-full py-3.5 rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] disabled:bg-[#1D4ED8]/60 text-white font-bold text-sm shadow-md transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                {isSavingCabor ? (
                  <>
                    <Icon icon="ph:spinner-bold" className="w-4 h-4 animate-spin" />
                    <span>Menyimpan...</span>
                  </>
                ) : (
                  <span>Selesai</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientRacketProfileView;
